import React, { useState, useEffect } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import { patientPageWidth } from '../../constant';

import {
    Header,
    PatientInfoCard,
    ProfileMenu,
    ConfirmDialog,
    HealthCard,
    ContactCard,
    MedicalRecordCard,
    EditProfileCard,
    Text,
} from '../../components/index';
import { Fragment } from 'react';

import { GET_SESSIONS, GET_HOSPITAL } from '../../services/patient';

import { alertError } from '../../redux/stateActions';

const Profile = () => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const mobileS = useMediaQuery('(max-width:374.95px)');

    const user = useSelector((state) => state.user);

    const [selectedMenu, setSelectedMenu] = useState('health');
    const [showSignOutDialog, setShowSignOutDialog] = useState(false);

    useEffect(() => {
        if (mdUp) {
            if (selectedMenu === '') {
                setSelectedMenu('health');
            }
        } else {
            if (selectedMenu === 'health') {
                setSelectedMenu('');
            }
        }
    }, [mdUp]);

    const handleMenu = (menu) => {
        setSelectedMenu(menu);
    };

    const handleClearSelectedMenu = () => {
        if (mdUp) {
            setSelectedMenu('health');
        } else {
            setSelectedMenu('');
        }
    };

    const handleSingOut = () => {
        localStorage.clear();
        window.location.replace('/signin');
    };

    /*---------- Get Session End Data ----------*/
    const [sessionData, setSessionData] = useState([]);

    const { data: getSessionData, error: getSessionError } = useQuery(GET_SESSIONS, {
        variables: { id: user.patient.id },
        skip: _.isEmpty(user),
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot get Medical Record: ${getSessionError.message}`));
        }
        if (getSessionData) {
            const filteredSessions = getSessionData.sessions.filter(
                (session) =>
                    session.patient_summaries[0].joinTime !== null &&
                    session.patient_summaries[0].leaveTime !== null
            );

            setSessionData(filteredSessions);
        }
    }, [getSessionError, getSessionData]);

    /*---------- Get Hospital Data ----------*/
    const [hospitalData, setHospitalData] = useState({});

    const { data: getHospitalData, error: getHospitalError } = useQuery(GET_HOSPITAL, {
        variables: { id: user.patient.hospital.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getHospitalError) {
            dispatch(alertError(`Cannot get Counseling Service data: ${getHospitalError.message}`));
        }
        if (getHospitalData) {
            setHospitalData(getHospitalData.hospital);
        }
    }, [getHospitalError, getHospitalData]);

    return (
        <div
            style={{
                padding: mobileS ? '0 5px 80px' : '0 15px 80px',
                width: '100%',
                maxWidth: patientPageWidth,
            }}
            data-test="profile-component"
        >
            {(mdUp || (!mdUp && selectedMenu === '')) && (
                <Header showNotice childrenContainerStyle={{ textAlign: 'left' }}>
                    <Text $fontSize="24px" $bold>
                        My Profile
                    </Text>
                </Header>
            )}
            <ConfirmDialog
                id="confirm-signout"
                description="modal-for-confirm-signout"
                open={showSignOutDialog}
                contentText="Are you sure you want to sign out ?"
                cancelText="Cancel"
                onCancel={() => setShowSignOutDialog(false)}
                confirmText="Sign out"
                confirmBackgroundColor="pink"
                onConfirm={handleSingOut}
            />
            {mdUp ? (
                <Fragment>
                    <PatientInfoCard
                        user={user}
                        $avatarSize={mobileS ? '60px' : mdUp ? '120px' : '80px'}
                        mdUp={mdUp}
                        mobileS={mobileS}
                        avatarUploader
                    />
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 2fr',
                            columnGap: '15px',
                            marginTop: '20px',
                        }}
                    >
                        {
                            <ProfileMenu
                                onClick={handleMenu}
                                selectedMenu={selectedMenu}
                                onSignOut={() => setShowSignOutDialog(true)}
                                mobileS={mobileS}
                            />
                        }

                        {selectedMenu === 'record' ? (
                            <MedicalRecordCard
                                onGoBack={handleClearSelectedMenu}
                                data={sessionData}
                                mdUp={mdUp}
                                mobileS={mobileS}
                                user={user}
                            />
                        ) : selectedMenu === 'health' ? (
                            <HealthCard
                                onGoBack={handleClearSelectedMenu}
                                mdUp={mdUp}
                                mobileS={mobileS}
                            />
                        ) : selectedMenu === 'profile' ? (
                            <EditProfileCard onGoBack={handleClearSelectedMenu} mdUp={mdUp} />
                        ) : (
                            <ContactCard
                                data={hospitalData}
                                onGoBack={handleClearSelectedMenu}
                                mdUp={mdUp}
                            />
                        )}
                    </div>
                </Fragment>
            ) : (
                <Fragment>
                    {selectedMenu === 'record' ? (
                        <MedicalRecordCard
                            data={sessionData}
                            onGoBack={handleClearSelectedMenu}
                            mdUp={mdUp}
                            mobileS={mobileS}
                            user={user}
                        />
                    ) : selectedMenu === 'health' ? (
                        <HealthCard
                            $transparent
                            onGoBack={handleClearSelectedMenu}
                            mdUp={mdUp}
                            mobileS={mobileS}
                        />
                    ) : selectedMenu === 'profile' ? (
                        <EditProfileCard onGoBack={handleClearSelectedMenu} mdUp={mdUp} />
                    ) : selectedMenu === 'contact' ? (
                        <ContactCard
                            data={hospitalData}
                            onGoBack={handleClearSelectedMenu}
                            mdUp={mdUp}
                        />
                    ) : (
                        <Fragment>
                            <PatientInfoCard
                                user={user}
                                $avatarSize={mobileS ? '60px' : mdUp ? '120px' : '80px'}
                                mdUp={mdUp}
                                mobileS={mobileS}
                                avatarUploader
                            />
                            <div style={{ marginTop: '20px' }}>
                                <ProfileMenu
                                    onClick={handleMenu}
                                    selectedMenu={selectedMenu}
                                    onSignOut={() => setShowSignOutDialog(true)}
                                    mobileLayout
                                    mobileS={mobileS}
                                />
                            </div>
                        </Fragment>
                    )}
                </Fragment>
            )}
        </div>
    );
};

export default Profile;
